import { env } from '@/config/env';
import { tokenStore } from '@/utils/tokenStore';

type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';


export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

// One in-flight refresh promise shared across concurrent requests in the same tab.
// Prevents multiple simultaneous /auth/refresh calls from the same tab.
let refreshingPromise: Promise<boolean> | null = null;

// Prevent multiple simultaneous redirects (e.g. from concurrent failing queries)
let redirectingToLogin = false;

// Mirrors AuthContext's LOGGED_IN_KEY: evidence that a refresh cookie should exist.
const LOGGED_IN_KEY = 'andhub_logged_in';

function mightHaveSession(): boolean {
  try {
    return localStorage.getItem(LOGGED_IN_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Gate every outgoing request on having an access token.
 *
 * The access token lives in memory only, so right after a full page load it is
 * null while AuthProvider's /auth/refresh is still in flight. AuthProvider is a
 * parent, and child effects run first, so components like ScrollToTop (pageview),
 * ScreenWatermark (settings/watermark, me/ip) and TaraPanel (voice/health) used to
 * fire before the token existed — going out with no Authorization header, 401ing,
 * and only succeeding on the automatic retry. That's the console noise.
 *
 * Refreshing here shares the 'andhub_refresh' Web Lock with AuthProvider, so this
 * queues behind an in-flight bootstrap refresh and then sees the token already set
 * (same tab, same tokenStore module) instead of refreshing a second time.
 */
async function ensureToken(): Promise<void> {
  // A refresh started by this module is already running — wait for it.
  if (refreshingPromise) {
    await refreshingPromise;
    return;
  }
  if (tokenStore.getAccessToken()) return;
  // No prior session (logged out, /login, first visit) — send it unauthenticated
  // and let the caller handle the 401 as before.
  if (!mightHaveSession()) return;
  await tryRefresh();
}

function forceLogout(): void {
  if (redirectingToLogin) return;
  redirectingToLogin = true;
  // clearAccessToken broadcasts LOGGED_OUT so all other tabs also log out
  tokenStore.clearAccessToken();
  try { sessionStorage.removeItem('andhub_user'); } catch { /* ignore */ }
  try { localStorage.removeItem('andhub_logged_in'); } catch { /* ignore */ }
  window.location.href = '/login';
}

async function tryRefresh(): Promise<boolean> {
  // Token already set (e.g. another concurrent request in this tab just refreshed)
  if (tokenStore.getAccessToken()) return true;
  // Another request in this same tab is already refreshing — wait for it
  if (refreshingPromise) return refreshingPromise;

  refreshingPromise = tokenStore
    .runWithRefreshLock(async () => {
      // Inside the lock: skip if another tab already refreshed and broadcast the token
      if (tokenStore.getAccessToken()) return;
      const { refreshSession } = await import('./auth');
      const data = await refreshSession(); // httpOnly cookie sent automatically
      tokenStore.setAccessToken(data.accessToken); // broadcasts to other tabs
    })
    .then(() => !!tokenStore.getAccessToken())
    .catch((err: unknown) => {
      if (err instanceof ApiError && err.status === 401) {
        // Refresh token is dead — kick everyone out
        forceLogout();
      } else {
        tokenStore.broadcastRefreshTransientFail();
      }
      return false;
    })
    .finally(() => { refreshingPromise = null; });

  return refreshingPromise;
}

async function request<T>(method: HttpMethod, path: string, body?: unknown, _retried = false): Promise<T> {
  // Wait for (or kick off) the session bootstrap so we never send a bare request.
  await ensureToken();

  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  const token = tokenStore.getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;


  const response = await fetch(`${env.apiUrl}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  // Token expired or missing — attempt one silent refresh then retry
  if (response.status === 401) {
    const data = await response.json().catch(() => ({})) as {
      error?: { code?: string; message?: string };
    };

    const code = data.error?.code;
    // TOKEN_EXPIRED: JWT invalid/expired
    // UNAUTHORIZED: no Bearer header (token was in-memory but page was reloaded)
    // SESSION_EXPIRED: Redis session gone (backend restart, TTL, or post-migration first request)
    // TOKEN_REVOKED: token was explicitly revoked (logout on another device)
    if (!_retried && (code === 'TOKEN_EXPIRED' || code === 'UNAUTHORIZED' || code === 'SESSION_EXPIRED' || code === 'TOKEN_REVOKED')) {
      // Silently clear the stale in-memory token — does NOT broadcast logout to other tabs
      tokenStore.invalidateAccessToken();
      const ok = await tryRefresh();
      if (ok) return request<T>(method, path, body, true);
    }

    throw new ApiError(
      response.status,
      code ?? 'UNAUTHORIZED',
      data.error?.message ?? response.statusText,
    );
  }

  if (response.status === 429) {
    throw new ApiError(429, 'RATE_LIMITED', 'Too many requests');
  }

  if (!response.ok) {
    const data = await response.json().catch(() => ({})) as {
      error?: { code?: string; message?: string };
    };
    throw new ApiError(
      response.status,
      data.error?.code ?? 'UNKNOWN_ERROR',
      data.error?.message ?? response.statusText,
    );
  }

  if (response.status === 204) return undefined as T;
  return response.json() as Promise<T>;
}

async function requestFormData<T>(method: 'POST' | 'PATCH', path: string, formData: FormData, _retried = false): Promise<T> {
  await ensureToken();

  const headers: Record<string, string> = {};
  const token = tokenStore.getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;


  const response = await fetch(`${env.apiUrl}${path}`, { method, headers, body: formData });

  if (response.status === 401) {
    const data = await response.json().catch(() => ({})) as { error?: { code?: string; message?: string } };
    const code = data.error?.code;
    if (!_retried && (code === 'TOKEN_EXPIRED' || code === 'UNAUTHORIZED' || code === 'SESSION_EXPIRED' || code === 'TOKEN_REVOKED')) {
      tokenStore.invalidateAccessToken();
      const ok = await tryRefresh();
      if (ok) return requestFormData<T>(method, path, formData, true);
    }
    throw new ApiError(response.status, code ?? 'UNAUTHORIZED', data.error?.message ?? response.statusText);
  }

  if (response.status === 429) {
    throw new ApiError(429, 'RATE_LIMITED', 'Too many requests');
  }

  if (!response.ok) {
    const data = await response.json().catch(() => ({})) as { error?: { code?: string; message?: string } };
    throw new ApiError(response.status, data.error?.code ?? 'UNKNOWN_ERROR', data.error?.message ?? response.statusText);
  }

  return response.json() as Promise<T>;
}

async function requestBlob(path: string, _retried = false): Promise<Blob | null> {
  await ensureToken();

  const headers: Record<string, string> = {};
  const token = tokenStore.getAccessToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;


  const response = await fetch(`${env.apiUrl}${path}`, { headers });

  if (response.status === 401) {
    const data = await response.json().catch(() => ({})) as {
      error?: { code?: string };
    };
    const code = data.error?.code;
    if (!_retried && (code === 'TOKEN_EXPIRED' || code === 'UNAUTHORIZED' || code === 'SESSION_EXPIRED' || code === 'TOKEN_REVOKED')) {
      tokenStore.invalidateAccessToken();
      const ok = await tryRefresh();
      if (ok) return requestBlob(path, true);
    }
    return null;
  }

  if (!response.ok) return null;
  return response.blob();
}

/** Returns the client IP as seen by the server. Non-blocking — fails silently. */
export async function getClientIp(): Promise<string | null> {
  try {
    const data = await request<{ ip: string | null }>('GET', '/api/me/ip');
    return data.ip;
  } catch {
    return null;
  }
}

export const api = {
  get: <T>(path: string) => request<T>('GET', path),
  post: <T>(path: string, body?: unknown) => request<T>('POST', path, body),
  put: <T>(path: string, body?: unknown) => request<T>('PUT', path, body),
  patch: <T>(path: string, body?: unknown) => request<T>('PATCH', path, body),
  delete: <T>(path: string) => request<T>('DELETE', path),
  getBlob: (path: string) => requestBlob(path),
  postForm: <T>(path: string, formData: FormData) => requestFormData<T>('POST', path, formData),
};
